import React, { useContext, useMemo, useRef, useState } from 'react';

import { EditDocumentInput } from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import saveTokens from '@amzn/meridian-tokens/base/icon/save';
import undoTokens from '@amzn/meridian-tokens/base/icon/undo';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import SearchField from '@amzn/meridian/search-field';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Tooltip from '@amzn/meridian/tooltip';

import { RECORD_KEEPER_TENANT_ID, recordKeeperClient } from 'src/api/recordKeeper/RecordKeeperClient';
import { IDocumentDetail } from 'src/components/Agreements/DocumentDetail/DocumentDetail';
import { AppContext } from 'src/contexts/appContext';

const BASE_HEADING_LEVEL = 5;
const SUB_HEADING_LEVEL = 6;

export const EditDocumentDetail: React.FC<IDocumentDetail> = (props) => {
    const { setLoading, setErrorMessage } = props;
    const { entities, metadata, userInfo } = useContext(AppContext);
    const [services, setServices] = useState(props.data?.services?.join(', ') ?? '');

    const [friendlyName, setFriendlyName] = useState(props.data.friendlyName);
    const [description, setDescription] = useState(props.data.description);
    const [licensors, setLicensors] = useState<string[]>(props.data?.licensors ?? []);
    const [licensorSearch, setLicensorSearch] = useState('');
    const [showResults, setShowResults] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const [startDate, setStartDate] = useState(props.data?.startDate ?? '');
    const [endDate, setEndDate] = useState(props.data?.endDate ?? '');
    const [contractCentral, setContractCentral] = useState(props.data?.links?.['Contract Central'] ?? '');
    const [approvals, setApprovals] = useState(props.data?.links?.['Approvals'] ?? '');
    const [customLinks, setCustomLinks] = useState<Array<{ title: string; url: string }>>(
        Object.entries(props.data.links ?? {})
            .filter(([key]) => key !== 'Contract Central' && key !== 'Approvals')
            .map(([title, url]) => ({ title, url }))
    );

    const serviceOptions = Object.values(metadata?.serviceMapping ?? {})
        .map((s) => s.serviceName)
        .filter((name): name is string => !!name);

    const selectedServices = services
        ? services
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
        : [];

    const handleServiceChange = (value: string[]) => {
        if (value.includes('All')) {
            const allServices = serviceOptions.filter((s) => s !== 'All');
            setServices(allServices.join(', '));
        } else {
            setServices(value.join(', '));
        }
    };

    const filteredEntities = useMemo(() => {
        if (!licensorSearch) return [];
        return (
            entities?.filter((e) => e.entityName?.toLowerCase().includes(licensorSearch.toLowerCase())).slice(0, 10) ??
            []
        );
    }, [licensorSearch, entities]);

    const addLicensor = (name: string) => {
        if (name && !licensors.includes(name)) {
            setLicensors([...licensors, name]);
        }
        setLicensorSearch('');
        setShowResults(false);
    };

    const handleSave = async () => {
        if (!props.documentId) return;
        setLoading(true);
        const links = {
            ...(contractCentral && { 'Contract Central': contractCentral }),
            ...(approvals && { Approvals: approvals }),
            ...Object.fromEntries(customLinks.filter((l) => l.title && l.url).map((l) => [l.title, l.url]))
        };
        await recordKeeperClient
            .editDocument(
                EditDocumentInput.clone({
                    tenantId: RECORD_KEEPER_TENANT_ID,
                    documentId: props.documentId,
                    friendlyName: friendlyName,
                    description: description,
                    tags: {
                        services: { values: services.split(',').map((s) => s.trim()) },
                        licensors: { values: licensors },
                        startDate: { value: startDate },
                        endDate: { value: endDate }
                    },
                    links: Object.keys(links).length > 0 ? links : undefined,
                    username: userInfo.username
                })
            )
            .toPromise()
            .then(() => {
                window.location.reload();
            })
            .catch((e) => setErrorMessage(JSON.stringify(e)))
            .finally(() => setLoading(false));
    };

    const nameElements = [<Input value={friendlyName} onChange={setFriendlyName} placeholder={props.data.name} />];
    const nameWidths = ['fill'];

    if (props.showPrefix) {
        nameElements.splice(0, 0, <Heading level={BASE_HEADING_LEVEL}>{'Edit Document: '}</Heading>);
        nameWidths.splice(0, 0, 'fit');
    }

    const infoSection = (
        <>
            <Row widths={['fill', 'fit']} wrap={'down'} alignmentHorizontal={'end'}>
                <Row minWidth={360} widths={nameWidths}>
                    {nameElements}
                </Row>
                <Row alignmentHorizontal={'end'}>
                    <Tooltip position={'start'} title={'Save edits'}>
                        <Button type={'icon'} aria-label={'save'} onClick={() => handleSave()}>
                            <Icon tokens={saveTokens} />
                        </Button>
                    </Tooltip>
                    <Tooltip position={'start'} title={'Cancel edits'}>
                        <Button type={'icon'} aria-label={'cancel'} onClick={() => props.setEditMode(false)}>
                            <Icon tokens={undoTokens} />
                        </Button>
                    </Tooltip>
                    <Tooltip position={'start'} title={'Back to all documents'}>
                        <Button type={'icon'} aria-label={'close'} onClick={props.onClose}>
                            <Icon tokens={closeSmallTokens} />
                        </Button>
                    </Tooltip>
                </Row>
            </Row>
            {friendlyName && <Text color={'secondary'} type={'b100'}>{`Original file name: ${props.data.name}`}</Text>}
            <Heading level={SUB_HEADING_LEVEL}>Description</Heading>
            <Textarea placeholder={'Add a description...'} value={description ?? ''} onChange={setDescription} />
        </>
    );

    const classificationSection = (
        <>
            <Column spacing={'100'}>
                <Row spacing={'200'}>
                    <Text>Services</Text>
                    <Text type={'b100'} color={'secondary'}>
                        (comma-separated)
                    </Text>
                </Row>
                <Input value={services} onChange={setServices} />
                <Select value={selectedServices} onChange={handleServiceChange} placeholder="Select services...">
                    {serviceOptions.map((service) => (
                        <SelectOption key={service} value={service} label={service} />
                    ))}
                </Select>
            </Column>
            <Column spacing={'100'}>
                <Text>Licensors</Text>
                <div className="licensors-container">
                    {licensors.map((licensor, idx) => (
                        <Box key={idx} spacingInset={'100'} type={'outline'}>
                            <Row className="selected-licensor" spacing={'050'} alignmentVertical={'center'}>
                                {licensor}
                                <Button
                                    type={'tertiary'}
                                    size={'small'}
                                    onClick={() => setLicensors(licensors.filter((_, i) => i !== idx))}
                                    className="licensor-remove-button"
                                >
                                    ✕
                                </Button>
                            </Row>
                        </Box>
                    ))}
                </div>
                <div style={{ position: 'relative' }} ref={searchRef}>
                    <SearchField
                        value={licensorSearch}
                        onChange={(val) => {
                            setLicensorSearch(val);
                            setShowResults(val.length > 0);
                        }}
                        onSubmit={() => addLicensor(licensorSearch)}
                        placeholder="Search or enter vendor name..."
                    />
                    {showResults && filteredEntities.length > 0 && (
                        <div
                            style={{
                                position: 'absolute',
                                zIndex: 1000,
                                width: '100%'
                            }}
                        >
                            <Box backgroundColor={'principal'} maxHeight={'200px'} overflowY={'auto'} type={'outline'}>
                                <Column spacing={'0'}>
                                    {filteredEntities.map((entity) => (
                                        <Button
                                            key={entity.entityId}
                                            type={'tertiary'}
                                            onClick={() => addLicensor(entity.entityName ?? '')}
                                            className="search-result-button"
                                        >
                                            <Box spacingInset={'200'} width={'100%'}>
                                                <Text>{entity.entityName}</Text>
                                            </Box>
                                        </Button>
                                    ))}
                                </Column>
                            </Box>
                        </div>
                    )}
                </div>
            </Column>
            <Row spacing={'400'}>
                <Column spacing={'100'}>
                    <Row spacing={'100'} alignmentVertical={'center'}>
                        <Text>Start Date</Text>
                        <Text type={'b100'} color={'secondary'}>
                            (yyyy-mm-dd)
                        </Text>
                    </Row>
                    <Input value={startDate} onChange={setStartDate} />
                </Column>
                <Column spacing={'100'}>
                    <Row spacing={'100'} alignmentVertical={'center'}>
                        <Text>End Date</Text>
                        <Text type={'b100'} color={'secondary'}>
                            (yyyy-mm-dd)
                        </Text>
                    </Row>
                    <Input value={endDate} onChange={setEndDate} />
                </Column>
            </Row>
        </>
    );

    const referencesSection = (
        <>
            <Input label="Contract Central Link" value={contractCentral} onChange={setContractCentral} />
            <Input label="Approvals Link" value={approvals} onChange={setApprovals} />
            <Column spacing={'100'}>
                <Row spacing={'100'} widths={['fill', 'fit']}>
                    <Text>Additional Links</Text>
                    <Button
                        type={'tertiary'}
                        size={'small'}
                        onClick={() => setCustomLinks([...customLinks, { title: '', url: '' }])}
                    >
                        + Add Link
                    </Button>
                </Row>
                {customLinks.map((link, idx) => (
                    <Row key={idx} spacing={'100'}>
                        <Input
                            label="Title"
                            value={link.title}
                            onChange={(val) => {
                                const updated = [...customLinks];
                                updated[idx].title = val;
                                setCustomLinks(updated);
                            }}
                        />
                        <Input
                            label="URL"
                            value={link.url}
                            onChange={(val) => {
                                const updated = [...customLinks];
                                updated[idx].url = val;
                                setCustomLinks(updated);
                            }}
                        />
                        <Column spacing={'100'}>
                            <Text>&nbsp;</Text>
                            <Button
                                type={'tertiary'}
                                size={'small'}
                                onClick={() => setCustomLinks(customLinks.filter((_, i) => i !== idx))}
                            >
                                ✕
                            </Button>
                        </Column>
                    </Row>
                ))}
            </Column>
        </>
    );

    return (
        <Column height={'100%'} heights={['fill', 'fit']}>
            <Column>
                {infoSection}
                <Divider />
                <Heading level={SUB_HEADING_LEVEL}>Classification</Heading>
                {classificationSection}
                <Divider />
                <Heading level={SUB_HEADING_LEVEL}>References</Heading>
                {referencesSection}
            </Column>
        </Column>
    );
};
